import { useCallback, useEffect, useMemo, useState } from 'react'
import { FolderOpen, Loader2, X } from 'lucide-react'
import type { FlowDeliverableListItem, FlowMeta } from '../../types/flow'
import { getFlowTemplate, listFlowDeliverables } from '../../lib/flowApi'
import FlowDeliverableReader from './FlowDeliverableReader'

type Props = {
  open: boolean
  onClose: () => void
  conversationId: number | null
  flowSessionToken: string | null
  flowMeta: FlowMeta | null
  flowTemplateId?: number | null
  focusDeliverableId?: string | null
}

function buildListItems(
  schemaDeliverables: Array<{ id: string; title?: string; part?: string }>,
  sequence: string[],
  meta: FlowMeta | null,
  fetched: Array<{ id: number; deliverable_id: string; content: string; word_count?: number | null; generated_at: string }>,
): FlowDeliverableListItem[] {
  const specMap = new Map(schemaDeliverables.map(d => [d.id, d]))
  const contentMap = new Map(fetched.map(d => [d.deliverable_id, d]))
  const doneSet = new Set(meta?.deliverables_done ?? [])
  const currentId = meta?.phase === 'deliverables' ? meta.current_step_id : null
  const awaiting = meta?.awaiting_confirmation ?? false

  const orderedIds = sequence.length
    ? sequence
    : schemaDeliverables.map(d => d.id)

  return orderedIds.map(deliverableId => {
    const spec = specMap.get(deliverableId)
    const row = contentMap.get(deliverableId)
    let status: FlowDeliverableListItem['status'] = 'pending'
    if (doneSet.has(deliverableId)) {
      status = 'done'
    } else if (currentId === deliverableId && !awaiting) {
      status = 'in_progress'
    }
    return {
      deliverable_id: deliverableId,
      title: spec?.title || deliverableId,
      part: spec?.part,
      status,
      content: row?.content,
      word_count: row?.word_count,
      generated_at: row?.generated_at,
      db_id: row?.id,
    }
  })
}

export default function FlowDeliverablesDrawer({
  open,
  onClose,
  conversationId,
  flowSessionToken,
  flowMeta,
  flowTemplateId,
  focusDeliverableId,
}: Props) {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<FlowDeliverableListItem[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!conversationId) return
    setLoading(true)
    setError(null)
    try {
      const fetched = await listFlowDeliverables(conversationId, flowSessionToken)
      let schemaDeliverables: Array<{ id: string; title?: string; part?: string }> = []
      let sequence: string[] = []
      if (flowTemplateId) {
        try {
          const tpl = await getFlowTemplate(flowTemplateId)
          schemaDeliverables = tpl.schema?.deliverables ?? []
          sequence = tpl.schema?.deliverables_sequence ?? []
        } catch {
          /* fallback: liste depuis API uniquement */
        }
      }
      if (!schemaDeliverables.length && fetched.length) {
        schemaDeliverables = fetched.map(d => ({ id: d.deliverable_id, title: d.deliverable_id }))
        sequence = fetched.map(d => d.deliverable_id)
      }
      setItems(buildListItems(schemaDeliverables, sequence, flowMeta, fetched))
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Impossible de charger les livrables')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [conversationId, flowSessionToken, flowMeta, flowTemplateId])

  useEffect(() => {
    if (!open) return
    load()
  }, [open, load])

  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  useEffect(() => {
    if (focusDeliverableId && open) {
      setSelectedId(focusDeliverableId)
    }
  }, [focusDeliverableId, open])

  const grouped = useMemo(() => {
    const groups = new Map<string, FlowDeliverableListItem[]>()
    for (const item of items) {
      const key = item.part || 'Livrables'
      const list = groups.get(key) ?? []
      list.push(item)
      groups.set(key, list)
    }
    return Array.from(groups.entries())
  }, [items])

  const selected = items.find(i => i.deliverable_id === selectedId && i.status === 'done')

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <aside className="fixed inset-y-0 right-0 z-[80] flex w-full max-w-md flex-col border-l border-emerald-100/80 bg-white/95 shadow-2xl dark:border-emerald-900/40 dark:bg-zinc-900/95 sm:max-w-lg">
        {selected?.content ? (
          <FlowDeliverableReader
            title={selected.title}
            content={selected.content}
            onBack={() => setSelectedId(null)}
          />
        ) : (
          <div className="flex h-full flex-col">
            <header className="flex items-start justify-between gap-3 border-b border-emerald-100/80 px-4 py-4 dark:border-emerald-900/40">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-zinc-900 dark:text-white">
                  <FolderOpen className="h-5 w-5 text-emerald-500" />
                  Mes livrables
                </h2>
                {flowMeta && (
                  <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                    {flowMeta.deliverables_done.length} / {flowMeta.deliverable_total} générés
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-4 py-3">
              {loading && (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-zinc-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Chargement…
                </div>
              )}
              {error && (
                <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                  {error}
                </p>
              )}
              {!loading && !error && items.length === 0 && (
                <p className="py-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
                  Tes contenus apparaîtront ici au fur et à mesure du parcours.
                </p>
              )}
              {!loading && grouped.map(([part, partItems]) => (
                <div key={part} className="mb-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                    {part}
                  </p>
                  <ul className="space-y-1">
                    {partItems.map(item => (
                      <li key={item.deliverable_id}>
                        <button
                          type="button"
                          disabled={item.status !== 'done'}
                          onClick={() => item.status === 'done' && setSelectedId(item.deliverable_id)}
                          className={`flex w-full items-center gap-3 rounded-2xl border px-3 py-2.5 text-left text-sm transition ${
                            item.status === 'done'
                              ? 'border-emerald-200/80 bg-emerald-50/60 text-zinc-900 hover:bg-emerald-100/60 dark:border-emerald-800/50 dark:bg-emerald-950/20 dark:text-zinc-100 dark:hover:bg-emerald-900/30'
                              : 'border-zinc-200/80 bg-zinc-50/40 text-zinc-400 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-500'
                          }`}
                        >
                          <span className="w-5 shrink-0 text-center" aria-hidden>
                            {item.status === 'done' ? '✅' : item.status === 'in_progress' ? '⏳' : '○'}
                          </span>
                          <span className="min-w-0 flex-1 truncate font-medium">{item.title}</span>
                          {item.word_count != null && item.status === 'done' && (
                            <span className="shrink-0 text-xs text-zinc-500">{item.word_count} mots</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </aside>
    </>
  )
}
